import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getMcpLimitLog} from 'src/openapi/admin/apis/McpLimitLogApi';
import {useAppDispatch} from 'src/store/hooks';


import {
    mcpLimitLogDataActions,
    mcpLimitLogDataSelector,
    mcpLimitLogResultSelector,
} from '../slices/mcp-limit-log-data.slice';
import {mcpLimitLogStateSelector} from '../slices/mcp-limit-log-state.slice';
import {mapFiltersToApiRequest} from '../utils';

import type {McpLimitLogSortField} from '../slices/mcp-limit-log-state.slice';
import type {McpLimitLogCriteria} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {McpLimitLogsPage} from 'src/openapi/admin/models/McpLimitLogsPage';

type GetMcpLimitLogCacheKeys = CacheKeys<undefined, McpLimitLogSortField, McpLimitLogCriteria>;

type Props = {
    accountId: number,
};

export const useGetMcpLimitLog = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetMcpLimitLogCacheKeys) => {
        const filters = mapFiltersToApiRequest(cacheKeys.filters);
        return apiDispatch(
            getMcpLimitLog({
                accountId,
                mcpLimitPageRequest: {
                    pagination: {
                        pageSize: cacheKeys.pagination.pageSize,
                        currentPage: cacheKeys.pagination.currentPage,
                    },
                    ...filters,
                    sortDirection: cacheKeys.sortCriteria.direction,
                },
            }),
        );
    }, [accountId, apiDispatch]);

    const setLoadedData = useCallback((
        response: McpLimitLogsPage,
        cacheKeys: GetMcpLimitLogCacheKeys,
    ) => {
        dispatch(mcpLimitLogDataActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.events,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: mcpLimitLogDataSelector,
        stateSelector: mcpLimitLogStateSelector,
        resultSelector: mcpLimitLogResultSelector,
        actions: mcpLimitLogDataActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
